import { useStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { Composition } from './CompositionSettings';
import type { ControlSetting } from './CompositionSettings/CompositionControl';

export interface RawComposition {
  id: string;
  label?: string;
  internalName?: string;
  aspectRatio?: string;
  source?: Record<string, unknown>;
  controls?: ControlSetting[];
  thumbnail?: { url?: string };
}

export interface RawSubcategory {
  id: string;
  label?: string;
  order?: number;
  compositions?: RawComposition[];
}

export interface RawCategory {
  id: string;
  label?: string;
  order?: number;
  subcategories?: RawSubcategory[];
}

export interface SubcategoryWithCompositions {
  id: string;
  label: string;
  order: number;
  compositions: Composition[];
}

export interface CategoryWithSubcategories {
  id: string;
  label: string;
  order: number;
  subcategories: SubcategoryWithCompositions[];
}

interface SubcategoryFilter {
  subcategoryId: string;
  subcategoryLabel: string;
}

interface CompositionContextValue {
  // Data - nested structure
  categories: CategoryWithSubcategories[];

  // Category selection
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string | null) => void;

  // Subcategory filter (for "See All" views)
  subcategoryFilter: SubcategoryFilter | null;
  setSubcategoryFilter: (filter: SubcategoryFilter | null) => void;

  // Loading state
  isLoading: boolean;

  // Helper methods to access nested data
  selectedCategory: CategoryWithSubcategories | undefined;
  getSubcategoryById: (
    subcategoryId: string,
  ) => SubcategoryWithCompositions | undefined;
  getCompositionById: (compositionId: string) => Composition | undefined;
}

const CompositionContext = createContext<CompositionContextValue | undefined>(
  undefined,
);

export const useCompositionContext = () => {
  const context = useContext(CompositionContext);
  if (!context) {
    throw new Error(
      'useCompositionContext must be used within CompositionProvider',
    );
  }
  return context;
};

interface CompositionProviderProps {
  children: ReactNode;
}

export const CompositionProvider: React.FC<CompositionProviderProps> = ({
  children,
}) => {
  const logger = LogManager.getLogger('component:CompositionProvider');
  const { datoClientStore } = useStore();
  const userIdentity = useUserIdentity();

  const { creatorStudioEnableDesignTabs, creatorStudioEnableCompositionsTab } =
    useFlagsCombination(userIdentity.currentRole);

  // State
  const [categories, setCategories] = useState<CategoryWithSubcategories[]>([]);
  const [compositions, setCompositions] = useState<Composition[]>([]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [subcategoryFilter, setSubcategoryFilter] =
    useState<SubcategoryFilter | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch compositions
  useEffect(() => {
    const fetchCompositions = async () => {
      if (!creatorStudioEnableDesignTabs || !creatorStudioEnableCompositionsTab)
        return;

      setIsLoading(true);
      try {
        const raw =
          await datoClientStore.compositionRepository.getCategoriesWithCompositions();

        const categoriesArray: CategoryWithSubcategories[] = [];
        const compositions: Composition[] = [];

        if (Array.isArray(raw)) {
          raw.forEach((category: RawCategory) => {
            if (!category?.id) return;

            const subcategoriesArray: SubcategoryWithCompositions[] = [];
            // Process subcategories
            if (Array.isArray(category.subcategories)) {
              category.subcategories.forEach((subcategory: RawSubcategory) => {
                if (!subcategory?.id) return;

                const compositionsArray: Composition[] = [];

                // Process compositions
                if (Array.isArray(subcategory.compositions)) {
                  subcategory.compositions.forEach((comp: RawComposition) => {
                    if (!comp?.id) return;

                    const composition: Composition = {
                      id: comp.id,
                      label: comp.label ?? '',
                      internal_name: comp.internalName ?? '',
                      aspect_ratio: comp.aspectRatio ?? '',
                      source:
                        typeof comp.source === 'object' && comp.source !== null
                          ? comp.source
                          : {},
                      controls: Array.isArray(comp.controls)
                        ? comp.controls
                        : [],
                      thumbnail: comp.thumbnail,
                    };

                    compositionsArray.push(composition);
                    // Build lookup map for O(1) access
                    compositions.push(composition);
                  });
                }

                subcategoriesArray.push({
                  id: subcategory.id,
                  label: subcategory.label ?? '',
                  order: subcategory.order ?? 0,
                  compositions: compositionsArray,
                });
              });
            }

            // Sort subcategories by order
            subcategoriesArray.sort((a, b) => a.order - b.order);

            categoriesArray.push({
              id: category.id,
              label: category.label ?? '',
              order: category.order ?? 0,
              subcategories: subcategoriesArray,
            });
          });
        }

        // Sort categories by order
        const sortedCategories = categoriesArray.sort(
          (a, b) => a.order - b.order,
        );

        setCategories(sortedCategories);
        setCompositions(compositions);
      } catch (error) {
        logger.warn('Failed to fetch compositions', { error });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompositions();
  }, [
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    datoClientStore.compositionRepository,
  ]);

  // Helper methods
  const getSelectedCategory = useMemo(() => {
    if (!selectedCategoryId) return undefined;
    return categories.find(cat => cat.id === selectedCategoryId);
  }, [selectedCategoryId, categories]);

  const getSubcategoryById = (subcategoryId: string) => {
    for (const category of categories) {
      const subcategory = category.subcategories.find(
        sub => sub.id === subcategoryId,
      );
      if (subcategory) return subcategory;
    }
    return undefined;
  };

  const getCompositionById = useCallback(
    (compositionId: string) => {
      return compositions.find(comp => comp.id === compositionId);
    },
    [compositions],
  );

  const value: CompositionContextValue = {
    categories,
    selectedCategoryId,
    setSelectedCategoryId,
    subcategoryFilter,
    setSubcategoryFilter,
    isLoading,
    selectedCategory: getSelectedCategory,
    getSubcategoryById,
    getCompositionById,
  };

  return (
    <CompositionContext.Provider value={value}>
      {children}
    </CompositionContext.Provider>
  );
};
